import { Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'

import AppLayout from '../components/AppLayout'
import HttpErrorPage from '../components/error-pages/HttpErrorPage'
import RouteErrorPage from '../components/error-pages/RouteErrorPage'
import ProtectedRoute from '../components/ProtectedRoute'
import { TableSkeleton } from '../components/ui/LoadingSkeleton'

import { lazyPage, registerLazyRoutes } from './lazyPage'

// ─── Lazy-loaded route components ─────────────────────────────────────
// Every feature page is split into its own chunk so the initial JS
// bundle stays small. Login + AppLayout + a few critical shells are kept
// eager because they're needed on every navigation.
const LoginPage = lazyPage(() => import('../features/auth/LoginPage'))

const DashboardPage = lazyPage(() => import('../features/DashboardPage'))
const AccountsPage = lazyPage(() => import('../features/accounts/AccountsPage'))
const AccountBranchPage = lazyPage(() => import('../features/accounts/AccountBranchPage'))
const CostCentersReportPage = lazyPage(() => import('../features/reports/CostCentersReportPage'))
const BookingConfirmationsPage = lazyPage(() => import('../features/crm/BookingConfirmationsPage'))
const JournalListPage = lazyPage(() => import('../features/journal/JournalListPage'))
const JournalEntryForm = lazyPage(() => import('../features/journal/JournalEntryForm'))
const VoucherForm = lazyPage(() => import('../features/vouchers/VoucherForm'))
const InvoiceForm = lazyPage(() => import('../features/vouchers/InvoiceForm'))
const TrialBalancePage = lazyPage(() => import('../features/reports/TrialBalancePage'))
const BalanceSheetPage = lazyPage(() => import('../features/reports/BalanceSheetPage'))
const IncomeStatementPage = lazyPage(() => import('../features/reports/IncomeStatementPage'))
const GeneralLedgerPage = lazyPage(() => import('../features/reports/GeneralLedgerPage'))
const TransactionReportPage = lazyPage(() => import('../features/reports/TransactionReportPage'))
const UnitsPage = lazyPage(() => import('../features/items/UnitsPage'))
const ItemGroupsPage = lazyPage(() => import('../features/items/ItemGroupsPage'))
const WarehousesPage = lazyPage(() => import('../features/inventory/WarehousesPage'))
const ItemsPage = lazyPage(() => import('../features/items/ItemsPage'))
const ItemForm = lazyPage(() => import('../features/items/ItemForm'))
const StockMovementForm = lazyPage(() => import('../features/inventory/StockMovementForm'))
const StockBalancesPage = lazyPage(() => import('../features/inventory/StockBalancesPage'))
const ItemLedgerPage = lazyPage(() => import('../features/inventory/ItemLedgerPage'))
const DepartmentsPage = lazyPage(() => import('../features/hr/DepartmentsPage'))
const JobTitlesPage = lazyPage(() => import('../features/hr/JobTitlesPage'))
const EmployeesPage = lazyPage(() => import('../features/hr/EmployeesPage'))
const EmployeeForm = lazyPage(() => import('../features/hr/EmployeeForm'))
const ShiftsPage = lazyPage(() => import('../features/hr/ShiftsPage'))
const RosterPage = lazyPage(() => import('../features/hr/RosterPage'))
const ShiftAssignmentsPage = lazyPage(() => import('../features/hr/ShiftAssignmentsPage'))
const DayExceptionsPage = lazyPage(() => import('../features/hr/DayExceptionsPage'))
const HrAlertsPage = lazyPage(() => import('../features/hr/HrAlertsPage'))
const SeparationsPage = lazyPage(() => import('../features/hr/SeparationsPage'))
const HrLettersPage = lazyPage(() => import('../features/hr/HrLettersPage'))
const OrgChartPage = lazyPage(() => import('../features/hr/OrgChartPage'))
const FlexReportPage = lazyPage(() => import('../features/hr/FlexReportPage'))
const InstantPunchPage = lazyPage(() => import('../features/hr/InstantPunchPage'))
const DevicesPage = lazyPage(() => import('../features/hr/DevicesPage'))
const PromotionsPage = lazyPage(() => import('../features/hr/PromotionsPage'))
const TasksPage = lazyPage(() => import('../features/hr/TasksPage'))
const HrDashboardPage = lazyPage(() => import('../features/hr/HrDashboardPage'))
const PenaltyWaiversPage = lazyPage(() => import('../features/hr/PenaltyWaiversPage'))
const OpenEndedReportPage = lazyPage(() => import('../features/hr/OpenEndedReportPage'))
const PublicHolidaysPage = lazyPage(() => import('../features/hr/PublicHolidaysPage'))
const AttendancePage = lazyPage(() => import('../features/hr/AttendancePage'))
const LeaveTypesPage = lazyPage(() => import('../features/hr/LeaveTypesPage'))
const LeaveRequestsPage = lazyPage(() => import('../features/hr/LeaveRequestsPage'))
const LeaveBalancesPage = lazyPage(() => import('../features/hr/LeaveBalancesPage'))
const SalaryComponentsPage = lazyPage(() => import('../features/hr/SalaryComponentsPage'))
const PayrollRunsPage = lazyPage(() => import('../features/hr/PayrollRunsPage'))
const MediaPayoutsPage = lazyPage(() => import('../features/hr/MediaPayoutsPage'))
const PayrollRunDetail = lazyPage(() => import('../features/hr/PayrollRunDetail'))
const LoansPage = lazyPage(() => import('../features/hr/LoansPage'))
const ViolationsPage = lazyPage(() => import('../features/hr/ViolationsPage'))
const PerformancePage = lazyPage(() => import('../features/hr/PerformancePage'))
const TrainingPage = lazyPage(() => import('../features/hr/TrainingPage'))
const MyProfilePage = lazyPage(() => import('../features/self-service/MyProfile'))
const UsersPage = lazyPage(() => import('../features/users/UsersPage'))
const TeamMapPage = lazyPage(() => import('../features/users/team-map/TeamMapPage'))
/** المسار القديم لخريطة الفرق — يُحوَّل إلى مكانها بالمبيعات مع معاملات العرض */
function TeamMapRedirect() {
  const { search } = useLocation()
  return <Navigate to={`/sales/team-map${search}`} replace />
}
const UnitStatusPage = lazyPage(() => import('../features/real-estate/UnitStatusPage'))
const CrmLookupsPage = lazyPage(() => import('../features/crm/CrmLookupsPage'))
const MyCallTasksPage = lazyPage(() => import('../features/crm/MyCallTasksPage'))
const CustomersPage = lazyPage(() => import('../features/crm/CustomersPage'))
const CustomerBooksPage = lazyPage(() => import('../features/crm/CustomerBooksPage'))
const CallCenterReportPage = lazyPage(() => import('../features/crm/CallCenterReportPage'))
const CustomerBookForm = lazyPage(() => import('../features/crm/CustomerBookForm'))
const MediaLeadsBoard = lazyPage(() => import('../features/crm/MediaLeadsBoard'))
const MediaLeadsTrashPage = lazyPage(() => import('../features/crm/MediaLeadsTrashPage'))
const UnifiedNumbersReport = lazyPage(() => import('../features/crm/UnifiedNumbersReport'))
const MediaPerformanceReport = lazyPage(() => import('../features/crm/MediaPerformanceReport'))
const CustomerServiceFollowUpPage = lazyPage(
  () => import('../features/crm/CustomerServiceFollowUpPage'),
)
const SalesActionsReport = lazyPage(() => import('../features/crm/SalesActionsReport'))
const BranchesFollowUpReport = lazyPage(() => import('../features/crm/BranchesFollowUpReport'))
const SaleContractsPage = lazyPage(() => import('../features/sales/SaleContractsPage'))
const DueInstallmentsPage = lazyPage(() => import('../features/sales/DueInstallmentsPage'))
const CommissionsPage = lazyPage(() => import('../features/sales/CommissionsPage'))
const SalesTargetsPage = lazyPage(() => import('../features/sales/SalesTargetsPage'))
const InboxPage = lazyPage(() => import('../features/crm/InboxPage'))
const InboxChannelsPage = lazyPage(() => import('../features/crm/InboxChannelsPage'))
const MarketingCatalogPage = lazyPage(() => import('../features/marketing/MarketingCatalogPage'))
const AppChatPage = lazyPage(() => import('../features/marketing/AppChatPage'))
const AppAccountsPage = lazyPage(() => import('../features/marketing/AppAccountsPage'))
const BannersPage = lazyPage(() => import('../features/marketing/BannersPage'))
const HomeSectionsPage = lazyPage(() => import('../features/marketing/HomeSectionsPage'))
const DevelopersPage = lazyPage(() => import('../features/marketing/DevelopersPage'))
const RealEstatePartnersPage = lazyPage(
  () => import('../features/marketing/RealEstatePartnersPage'),
)
const PartnerListingsPage = lazyPage(() => import('../features/marketing/PartnerListingsPage'))
const FunnelPage = lazyPage(() => import('../features/marketing/FunnelPage'))
const TrustPage = lazyPage(() => import('../features/marketing/TrustPage'))
const AppCampaignsPage = lazyPage(() => import('../features/marketing/AppCampaignsPage'))
const RecruitmentPage = lazyPage(() => import('../features/hr/RecruitmentPage'))
const AnnouncementsPage = lazyPage(() => import('../features/hr/AnnouncementsPage'))
const TaxRulesPage = lazyPage(() => import('../features/hr/TaxRulesPage'))
const FileCompletenessPage = lazyPage(() => import('../features/hr/FileCompletenessPage'))
const JobApplicationPage = lazyPage(() => import('../features/public/JobApplicationPage'))
const ProjectLandingPage = lazyPage(() => import('../features/public/ProjectLandingPage'))
const PreliminaryReservationsPage = lazyPage(
  () => import('../features/sales/PreliminaryReservationsPage'),
)
const CampaignsPage = lazyPage(() => import('../features/marketing/CampaignsPage'))
const CampaignDisbursementsPage = lazyPage(
  () => import('../features/marketing/CampaignDisbursementsPage'),
)
const ContentCalendarPage = lazyPage(() => import('../features/marketing/ContentCalendarPage'))
const ProductionBoardPage = lazyPage(() => import('../features/marketing/ProductionBoardPage'))
const MarketingSuppliersPage = lazyPage(() => import('../features/marketing/SuppliersPage'))
const MarketingBudgetPage = lazyPage(() => import('../features/marketing/MarketingBudgetPage'))
const EffectivenessReportPage = lazyPage(() => import('../features/marketing/EffectivenessReport'))
const ResidentialComplexesListPage = lazyPage(
  () => import('../features/crm/residential-complexes/ResidentialComplexesListPage'),
)
const ComplexTypeSelectPage = lazyPage(
  () => import('../features/crm/residential-complexes/ComplexTypeSelectPage'),
)
const VerticalComplexForm = lazyPage(
  () => import('../features/crm/residential-complexes/VerticalComplexForm'),
)
const HorizontalComplexForm = lazyPage(
  () => import('../features/crm/residential-complexes/HorizontalComplexForm'),
)
const ResidentialComplexDetailPage = lazyPage(
  () => import('../features/crm/residential-complexes/ResidentialComplexDetailPage'),
)
const ApartmentFeaturesPage = lazyPage(
  () => import('../features/crm/residential-complexes/ApartmentFeaturesPage'),
)
const ComplexFinanceHubPage = lazyPage(
  () => import('../features/crm/residential-complexes/ComplexFinanceHubPage'),
)
const ComplexFinancePage = lazyPage(
  () => import('../features/crm/residential-complexes/ComplexFinancePage'),
)
const CurrenciesPage = lazyPage(() => import('../features/lookups/CurrenciesPage'))
const CostCentersPage = lazyPage(() => import('../features/lookups/CostCentersPage'))
const FiscalYearsPage = lazyPage(() => import('../features/lookups/FiscalYearsPage'))

/**
 * Suspense-wrapped element. Use for every lazy() route so transitions
 * don't render a blank screen while the chunk is downloading.
 */
function L(element: ReactNode): ReactNode {
  return <Suspense fallback={<TableSkeleton />}>{element}</Suspense>
}

export const router = createBrowserRouter([
  {
    // صفحة خطأ موحّدة لكل المسارات — تلتقط فشل تحميل chunks النسخ القديمة بعد النشر
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/login', element: L(<LoginPage />) },
      // ═══ صفحة هبوط عامة لمشروع مسوَّق — روابط الحملات وأزرار «افتح في التطبيق» ═══
      { path: '/p/:id', element: L(<ProjectLandingPage />) },
      // ═══ استمارة التقديم للوظائف — رابط عام يُنشر بواتساب وفيسبوك ═══
      { path: '/jobs', element: L(<JobApplicationPage />) },
      // ═══ /m/* = تطبيق الموظفين (Flutter Web) — تخدمه nginx من جذر مستقل، لا من هذه اللوحة ═══
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { path: '/', element: L(<DashboardPage />) },
              // شاشات أُزيلت — روابطها القديمة (إشعارات محفوظة ومفضّلات) تعود للرئيسية بدل ٤٠٤
              ...[
                '/my/dashboard',
                '/my/leaves',
                '/my/payslips',
                '/my/complexes',
                '/my/complexes/:id',
                '/crm/customer-info',
                '/crm/customer-info/:id',
                '/crm/reports/visits',
                '/real-estate/purchase-budgets',
                '/real-estate/profit-report',
              ].map((path) => ({ path, element: <Navigate to="/" replace /> })),
              { path: '/accounts', element: L(<AccountsPage />) },
              {
                path: '/accounts/expenses',
                element: L(<AccountBranchPage key="expense" kind="expense" />),
              },
              {
                path: '/accounts/revenues',
                element: L(<AccountBranchPage key="revenue" kind="revenue" />),
              },
              { path: '/journal', element: L(<JournalListPage />) },
              { path: '/journal/new', element: L(<JournalEntryForm />) },
              { path: '/journal/:id', element: L(<JournalEntryForm />) },
              { path: '/opening', element: L(<JournalEntryForm opening />) },
              { path: '/vouchers/payment', element: L(<VoucherForm mode="payment" />) },
              { path: '/vouchers/receipt', element: L(<VoucherForm mode="receipt" />) },
              { path: '/invoices/sales', element: L(<InvoiceForm mode="sales" />) },
              { path: '/invoices/purchase', element: L(<InvoiceForm mode="purchase" />) },
              { path: '/reports/trial-balance', element: L(<TrialBalancePage />) },
              { path: '/reports/balance-sheet', element: L(<BalanceSheetPage />) },
              { path: '/reports/income-statement', element: L(<IncomeStatementPage />) },
              { path: '/reports/cost-centers', element: L(<CostCentersReportPage />) },
              { path: '/reports/general-ledger', element: L(<GeneralLedgerPage />) },
              { path: '/reports/sales', element: L(<TransactionReportPage kind="sales" />) },
              {
                path: '/reports/purchases',
                element: L(<TransactionReportPage kind="purchases" />),
              },
              {
                path: '/reports/receipt-vouchers',
                element: L(<TransactionReportPage kind="receipts" />),
              },
              {
                path: '/reports/payment-vouchers',
                element: L(<TransactionReportPage kind="payments" />),
              },
              // Inventory
              { path: '/items', element: L(<ItemsPage />) },
              { path: '/items/new', element: L(<ItemForm />) },
              { path: '/items/:id', element: L(<ItemForm />) },
              { path: '/item-groups', element: L(<ItemGroupsPage />) },
              { path: '/units', element: L(<UnitsPage />) },
              { path: '/warehouses', element: L(<WarehousesPage />) },
              { path: '/stock/new-movement', element: L(<StockMovementForm />) },
              { path: '/stock/balances', element: L(<StockBalancesPage />) },
              { path: '/stock/item-ledger', element: L(<ItemLedgerPage />) },
              // HR
              { path: '/employees', element: L(<EmployeesPage />) },
              { path: '/employees/new', element: L(<EmployeeForm />) },
              { path: '/employees/:id', element: L(<EmployeeForm />) },
              { path: '/departments', element: L(<DepartmentsPage />) },
              { path: '/job-titles', element: L(<JobTitlesPage />) },
              { path: '/shifts', element: L(<ShiftsPage />) },
              { path: '/roster', element: L(<RosterPage />) },
              { path: '/shift-assignments', element: L(<ShiftAssignmentsPage />) },
              { path: '/day-exceptions', element: L(<DayExceptionsPage />) },
              { path: '/hr-alerts', element: L(<HrAlertsPage />) },
              { path: '/separations', element: L(<SeparationsPage />) },
              { path: '/hr-letters', element: L(<HrLettersPage />) },
              { path: '/org-chart', element: L(<OrgChartPage />) },
              { path: '/flex-report', element: L(<FlexReportPage />) },
              { path: '/hr/instant-punch', element: L(<InstantPunchPage />) },
              { path: '/hr/devices', element: L(<DevicesPage />) },
              { path: '/hr/promotions', element: L(<PromotionsPage />) },
              { path: '/hr/tasks', element: L(<TasksPage />) },
              { path: '/hr/recruitment', element: L(<RecruitmentPage />) },
              { path: '/hr/announcements', element: L(<AnnouncementsPage />) },
              { path: '/hr/tax-rules', element: L(<TaxRulesPage />) },
              { path: '/hr/file-completeness', element: L(<FileCompletenessPage />) },
              { path: '/hr/dashboard', element: L(<HrDashboardPage />) },
              { path: '/hr/penalty-waivers', element: L(<PenaltyWaiversPage />) },
              { path: '/hr/open-ended-report', element: L(<OpenEndedReportPage />) },
              { path: '/public-holidays', element: L(<PublicHolidaysPage />) },
              { path: '/attendance', element: L(<AttendancePage />) },
              { path: '/leave-types', element: L(<LeaveTypesPage />) },
              { path: '/leaves', element: L(<LeaveRequestsPage />) },
              { path: '/leave-balances', element: L(<LeaveBalancesPage />) },
              { path: '/salary-components', element: L(<SalaryComponentsPage />) },
              { path: '/payroll/runs', element: L(<PayrollRunsPage />) },
              { path: '/hr/media-payouts', element: L(<MediaPayoutsPage />) },
              { path: '/payroll/runs/:id', element: L(<PayrollRunDetail />) },
              { path: '/loans', element: L(<LoansPage />) },
              { path: '/violations', element: L(<ViolationsPage />) },
              { path: '/performance', element: L(<PerformancePage />) },
              { path: '/training', element: L(<TrainingPage />) },
              // Self-Service
              { path: '/my/profile', element: L(<MyProfilePage />) },
              // CRM
              { path: '/crm/lookups', element: L(<CrmLookupsPage />) },
              { path: '/crm/customers', element: L(<CustomersPage />) },
              { path: '/crm/callcenter-report', element: L(<CallCenterReportPage />) },
              { path: '/crm/books', element: L(<CustomerBooksPage />) },
              { path: '/crm/books/new', element: L(<CustomerBookForm />) },
              { path: '/crm/booking-confirmations', element: L(<BookingConfirmationsPage />) },
              { path: '/crm/books/:id', element: L(<CustomerBookForm />) },
              { path: '/crm/media-leads', element: L(<MediaLeadsBoard />) },
              { path: '/crm/my-call-tasks', element: L(<MyCallTasksPage />) },
              { path: '/crm/team-call-tasks', element: L(<MyCallTasksPage />) },
              { path: '/crm/media-leads/deleted', element: L(<MediaLeadsTrashPage />) },
              { path: '/crm/reports/unified-numbers', element: L(<UnifiedNumbersReport />) },
              { path: '/crm/reports/media-performance', element: L(<MediaPerformanceReport />) },
              { path: '/crm/cs-followup', element: L(<CustomerServiceFollowUpPage />) },
              { path: '/crm/reports/sales-actions', element: L(<SalesActionsReport />) },
              { path: '/crm/reports/branches', element: L(<BranchesFollowUpReport />) },
              // Sales (عقود البيع)
              { path: '/sales/contracts', element: L(<SaleContractsPage />) },
              { path: '/sales/reservations', element: L(<PreliminaryReservationsPage />) },
              { path: '/sales/due-installments', element: L(<DueInstallmentsPage />) },
              { path: '/sales/commissions', element: L(<CommissionsPage />) },
              { path: '/sales/targets', element: L(<SalesTargetsPage />) },
              { path: '/crm/inbox', element: L(<InboxPage />) },
              { path: '/crm/inbox-channels', element: L(<InboxChannelsPage />) },
              // المدخل القديم بخدمة العملاء — الروابط المحفوظة تصل للمدخل الواحد
              { path: '/crm/app-chat', element: <Navigate to="/marketing/app-chat" replace /> },
              // المدخل نفسه من قسم التسويق العقاري
              { path: '/marketing/app-chat', element: L(<AppChatPage />) },
              // Marketing (التسويق)
              { path: '/marketing/catalog', element: L(<MarketingCatalogPage />) },
              { path: '/marketing/app-accounts', element: L(<AppAccountsPage />) },
              { path: '/marketing/banners', element: L(<BannersPage />) },
              { path: '/marketing/home-sections', element: L(<HomeSectionsPage />) },
              { path: '/marketing/developers', element: L(<DevelopersPage />) },
              { path: '/marketing/partners', element: L(<RealEstatePartnersPage />) },
              { path: '/marketing/partner-listings', element: L(<PartnerListingsPage />) },
              { path: '/marketing/funnel', element: L(<FunnelPage />) },
              { path: '/marketing/trust', element: L(<TrustPage />) },
              { path: '/marketing/app-campaigns', element: L(<AppCampaignsPage />) },
              { path: '/marketing/campaigns', element: L(<CampaignsPage />) },
              { path: '/marketing/content', element: L(<ContentCalendarPage />) },
              { path: '/marketing/production', element: L(<ProductionBoardPage />) },
              { path: '/marketing/suppliers', element: L(<MarketingSuppliersPage />) },
              { path: '/marketing/budget', element: L(<MarketingBudgetPage />) },
              { path: '/marketing/effectiveness', element: L(<EffectivenessReportPage />) },
              {
                path: '/vouchers/campaign-disbursements',
                element: L(<CampaignDisbursementsPage />),
              },
              // CRM - Residential Complexes (Real Estate)
              { path: '/crm/residential-complexes', element: L(<ResidentialComplexesListPage />) },
              { path: '/crm/residential-complexes/new', element: L(<ComplexTypeSelectPage />) },
              // الإدارة المالية: عمولات الموظفين وعمولة التسويق وطرق التسديد لكل مجمع
              {
                path: '/crm/residential-complexes/finance',
                element: L(<ComplexFinanceHubPage />),
              },
              {
                path: '/crm/residential-complexes/:id/finance',
                element: L(<ComplexFinancePage />),
              },
              {
                path: '/crm/residential-complexes/new/vertical',
                element: L(<VerticalComplexForm />),
              },
              {
                path: '/crm/residential-complexes/new/horizontal',
                element: L(<HorizontalComplexForm />),
              },
              {
                path: '/crm/residential-complexes/:id',
                element: L(<ResidentialComplexDetailPage />),
              },
              {
                path: '/crm/residential-complexes/:id/edit/vertical',
                element: L(<VerticalComplexForm />),
              },
              {
                path: '/crm/residential-complexes/:id/edit/horizontal',
                element: L(<HorizontalComplexForm />),
              },
              { path: '/crm/apartment-features', element: L(<ApartmentFeaturesPage />) },
              { path: '/currencies', element: L(<CurrenciesPage />) },
              { path: '/cost-centers', element: L(<CostCentersPage />) },
              { path: '/fiscal-years', element: L(<FiscalYearsPage />) },
              // Users Management (Admin only)
              { path: '/users', element: L(<UsersPage />) },
              // خريطة الفرق انتقلت إلى المبيعات — الروابط القديمة تُحوَّل بمعاملاتها
              { path: '/sales/team-map', element: L(<TeamMapPage />) },
              { path: '/users/team-map', element: <TeamMapRedirect /> },
              // Property Assignment — Real Estate
              { path: '/crm/unit-status', element: L(<UnitStatusPage />) },
            ],
          },
        ],
      },
      { path: '/403', element: <HttpErrorPage status="403" /> },
      { path: '/404', element: <HttpErrorPage status="404" /> },
      { path: '/500', element: <HttpErrorPage status="500" /> },
      { path: '*', element: <HttpErrorPage status="404" /> },
    ],
  },
])

// كل صفحةٍ كسولة بمسارٍ ثابت تُسجَّل لتحميلها مسبقاً من القائمة (تمرير الفأرة / وقت الفراغ)
registerLazyRoutes(router.routes)
